// FieldOpsToday — the tech-facing "today's queue" view.
//
// Shipped 2026-05-24 as the Tasks page primary view (Tom: "PMs assign,
// technicians execute"). Reads from GET /api/field-ops/today which already
// returns open generated_tasks for the current user, grouped by project,
// sorted by scheduled_date.
//
// Capabilities:
//   - Filter by scheduled date (Today / Tomorrow / This Week / All open)
//   - Mark Start / Complete / Block from the row
//   - Click through to the project (read-only context for now; the
//     full task detail / photo / voice-note UX lives in the PWA — #6)
//   - Management: pick another user to view their queue

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  IconButton,
  CircularProgress,
  Collapse,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PlayArrow as PlayIcon,
  Check as CheckIcon,
  Block as BlockIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format, addDays } from 'date-fns';
import {
  getFieldOpsToday,
  startFieldOpsTask,
  completeFieldOpsTask,
  blockFieldOpsTask,
  getUsers,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// Defense-in-depth: never let a malformed/leaked date crash the tech view.
// Same class as the bug Vin Baus hit on 2026-06-09 (Invalid time value
// blanked the entire project page). Field Work is the tech-facing surface;
// a blank page here = a tech who can't see today's work. Tom Clancy 2026-06-09.
const safeFormat = (value, fmt) => {
  if (value == null || value === '') return '';
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return format(d, fmt);
  } catch (_) {
    return '';
  }
};

const ACS_BLUE = '#0D2C54';

const STATUS_CHIP = {
  pending: { label: 'Pending', color: 'default' },
  in_progress: { label: 'In progress', color: 'warning' },
  blocked: { label: 'Blocked', color: 'error' },
  complete: { label: 'Complete', color: 'success' },
  completed: { label: 'Complete', color: 'success' },
};

const PHASE_LABEL = {
  1: 'P1 Pre-construction',
  2: 'P2 Rough-in',
  3: 'P3 Pre-final',
  4: 'P4 Final',
  5: 'P5 QC',
  6: 'P6 AfterCare',
};

// ---------- date filter ---------------------------------------------------

const DATE_FILTERS = [
  { value: 'today', label: 'Today' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'week', label: 'This week' },
  { value: 'all', label: 'All open' },
];

const dateFilterToScheduledDate = (filter) => {
  if (filter === 'today') return new Date().toISOString().split('T')[0];
  if (filter === 'tomorrow')
    return addDays(new Date(), 1).toISOString().split('T')[0];
  return null; // 'week' and 'all' do client-side filtering
};

// ---------- component -----------------------------------------------------

export default function FieldOpsToday() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMgmt =
    user?.role === 'Management' ||
    (Array.isArray(user?.roles) && user.roles.includes('Management'));

  const [filter, setFilter] = useState('today');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedProjects, setExpandedProjects] = useState({});
  const [busyTaskId, setBusyTaskId] = useState(null);
  const [blockDialog, setBlockDialog] = useState(null);
  const [blockReason, setBlockReason] = useState('');

  // Management can view another user's queue
  const [allUsers, setAllUsers] = useState([]);
  const [viewUserId, setViewUserId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      const sd = dateFilterToScheduledDate(filter);
      if (sd) params.scheduled_date = sd;
      if (viewUserId) params.user_id = viewUserId;
      const res = await getFieldOpsToday(params);
      setData(res.data);
      // Auto-expand projects by default
      const expand = {};
      (res.data.projects || []).forEach((p) => {
        expand[p.project_id] = true;
      });
      setExpandedProjects(expand);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load field tasks');
    } finally {
      setLoading(false);
    }
  }, [filter, viewUserId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!isMgmt) return;
    getUsers()
      .then((res) => setAllUsers(res.data || []))
      .catch(() => {});
  }, [isMgmt]);

  // Client-side filter for "This week"
  const visibleProjects = (() => {
    if (!data?.projects) return [];
    if (filter !== 'week') return data.projects;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekEnd = addDays(today, 7);
    return data.projects
      .map((p) => ({
        ...p,
        tasks: p.tasks.filter((t) => {
          if (!t.scheduled_date) return false;
          const sd = new Date(t.scheduled_date);
          return sd >= today && sd <= weekEnd;
        }),
      }))
      .filter((p) => p.tasks.length > 0);
  })();

  const totalCount = visibleProjects.reduce((sum, p) => sum + p.tasks.length, 0);

  // ---------- actions ----------------------------------------------------

  const handleStart = async (task) => {
    setBusyTaskId(task.id);
    try {
      await startFieldOpsTask(task.id);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not start task');
    } finally {
      setBusyTaskId(null);
    }
  };

  const handleComplete = async (task, force = false) => {
    setBusyTaskId(task.id);
    try {
      await completeFieldOpsTask(task.id, force ? { force: true } : {});
      await load();
    } catch (err) {
      const data = err.response?.data || {};
      if (data.error === 'photos_required' || data.error === 'os_items_unconfirmed') {
        // For now, surface the gate as an inline error. PWA build will
        // route the tech to the photo / OS confirmation flow inline.
        setError(data.message || 'Cannot complete this task yet.');
      } else {
        setError(data.error || 'Could not complete task');
      }
    } finally {
      setBusyTaskId(null);
    }
  };

  const handleBlock = async () => {
    if (!blockDialog || !blockReason.trim()) return;
    setBusyTaskId(blockDialog.id);
    try {
      await blockFieldOpsTask(blockDialog.id, { reason: blockReason.trim() });
      setBlockDialog(null);
      setBlockReason('');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not block task');
    } finally {
      setBusyTaskId(null);
    }
  };

  // ---------- render -----------------------------------------------------

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Controls bar */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, v) => v && setFilter(v)}
          size="small"
        >
          {DATE_FILTERS.map((f) => (
            <ToggleButton key={f.value} value={f.value} sx={{ textTransform: 'none', px: 2 }}>
              {f.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {isMgmt && allUsers.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>View queue for</InputLabel>
            <Select
              value={viewUserId || ''}
              label="View queue for"
              onChange={(e) => setViewUserId(e.target.value || null)}
            >
              <MenuItem value=""><em>Me</em></MenuItem>
              {allUsers
                .filter((u) => u.active !== false)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name} {u.role ? `· ${u.role}` : ''}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}

        <Box sx={{ flex: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {totalCount} task{totalCount === 1 ? '' : 's'}
        </Typography>
        <IconButton size="small" onClick={load} title="Refresh">
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {visibleProjects.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {filter === 'today' && 'No field work scheduled for today.'}
            {filter === 'tomorrow' && 'Nothing scheduled for tomorrow yet.'}
            {filter === 'week' && 'No tasks scheduled in the next 7 days.'}
            {filter === 'all' && 'No open field tasks assigned to you.'}
          </Typography>
          {filter !== 'all' && (
            <Button size="small" onClick={() => setFilter('all')} sx={{ mt: 1 }}>
              See all open tasks
            </Button>
          )}
        </Paper>
      ) : (
        visibleProjects.map((project) => {
          const isOpen = expandedProjects[project.project_id] !== false;
          return (
            <Paper key={project.project_id} sx={{ mb: 2, overflow: 'hidden' }}>
              {/* Project header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 2,
                  py: 1.5,
                  bgcolor: ACS_BLUE,
                  color: 'white',
                  cursor: 'pointer',
                }}
                onClick={() =>
                  setExpandedProjects((prev) => ({
                    ...prev,
                    [project.project_id]: !isOpen,
                  }))
                }
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {project.primary_job_number}
                  </Typography>
                  <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                    {project.project_name}
                  </Typography>
                  {project.project_address && (
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      {project.project_address}
                    </Typography>
                  )}
                </Box>
                <Chip
                  label={`${project.tasks.length} task${project.tasks.length === 1 ? '' : 's'}`}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', mr: 1 }}
                />
                <IconButton
                  size="small"
                  sx={{ color: 'white' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/projects/${project.project_id}`);
                  }}
                  title="Open project"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
                {isOpen ? (
                  <ExpandLessIcon sx={{ color: 'white' }} />
                ) : (
                  <ExpandMoreIcon sx={{ color: 'white' }} />
                )}
              </Box>

              <Collapse in={isOpen}>
                {project.tasks.map((task, idx) => {
                  const statusCfg = STATUS_CHIP[task.status] || { label: task.status, color: 'default' };
                  const isBusy = busyTaskId === task.id;
                  return (
                    <Box key={task.id}>
                      {idx > 0 && <Divider />}
                      <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        <Box sx={{ flex: 1, minWidth: 240 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                            <Chip
                              label={statusCfg.label}
                              size="small"
                              color={statusCfg.color}
                              sx={{ height: 22 }}
                            />
                            {task.scheduled_date && (
                              <Chip
                                label={`Scheduled ${safeFormat(task.scheduled_date, 'MMM d')}`}
                                size="small"
                                variant="outlined"
                                sx={{ height: 22 }}
                              />
                            )}
                            {task.due_date && (
                              <Chip
                                label={`Due ${safeFormat(task.due_date, 'MMM d')}`}
                                size="small"
                                variant="outlined"
                                color="warning"
                                sx={{ height: 22 }}
                              />
                            )}
                            {task.phase && (
                              <Typography variant="caption" color="text.secondary">
                                {PHASE_LABEL[task.phase] || `Phase ${task.phase}`}
                              </Typography>
                            )}
                          </Box>

                          <Typography variant="body1" fontWeight={500}>
                            {task.room_name && (
                              <Box component="span" sx={{ color: 'text.secondary' }}>
                                {task.room_name} ·{' '}
                              </Box>
                            )}
                            {task.task_name}
                            {task.bundle_count > 1 && (
                              <Box component="span" sx={{ color: 'text.secondary' }}>
                                {' '}
                                (×{task.bundle_count})
                              </Box>
                            )}
                          </Typography>

                          {task.notes && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                              {task.notes}
                            </Typography>
                          )}
                          {task.blocked_reason && (
                            <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                              Blocked: {task.blocked_reason}
                            </Typography>
                          )}

                          <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                            {task.photo_count > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                📷 {task.photo_count}
                              </Typography>
                            )}
                            {task.note_count > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                📝 {task.note_count}
                              </Typography>
                            )}
                            {task.unconfirmed_os_count > 0 && (
                              <Typography variant="caption" color="warning.main">
                                ⚠ {task.unconfirmed_os_count} OS to confirm
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        {/* Actions */}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          {task.status === 'pending' && (
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<PlayIcon />}
                              disabled={isBusy}
                              onClick={() => handleStart(task)}
                            >
                              Start
                            </Button>
                          )}
                          {(task.status === 'in_progress' || task.status === 'pending') && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              startIcon={<CheckIcon />}
                              disabled={isBusy}
                              onClick={() => handleComplete(task)}
                            >
                              Complete
                            </Button>
                          )}
                          {task.status !== 'blocked' && task.status !== 'complete' && task.status !== 'completed' && (
                            <IconButton
                              size="small"
                              color="error"
                              disabled={isBusy}
                              onClick={() => {
                                setBlockDialog(task);
                                setBlockReason('');
                              }}
                              title="Block task"
                            >
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          )}
                          {isBusy && <CircularProgress size={20} />}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Collapse>
            </Paper>
          );
        })
      )}

      {/* Block reason dialog */}
      <Dialog open={!!blockDialog} onClose={() => setBlockDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>Block this task</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {blockDialog?.task_name}
          </Typography>
          <TextField
            label="Reason"
            autoFocus
            fullWidth
            multiline
            rows={3}
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            placeholder="What's blocking this? (rough-in not done, missing material, OS pending, etc.)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!blockReason.trim()}
            onClick={handleBlock}
          >
            Block
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
